import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { GraduationCap, LogOut, BookOpen, Users, Award, TrendingUp } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import api from '../services/api';

const TeacherDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [loading, setLoading] = useState(true);

  // Determine active tab from URL path
  const getActiveTab = () => {
    const path = location.pathname;
    if (path.includes('/batches')) return 'batches';
    if (path.includes('/students')) return 'students';
    return 'dashboard';
  };
  const activeTab = getActiveTab();

  useEffect(() => {
    fetchDashboardData();
    // Redirect to dashboard if on base teacher path
    if (location.pathname === '/teacher' || location.pathname === '/teacher/') {
      navigate('/teacher/dashboard', { replace: true });
    }
  }, [location.pathname, navigate]);

  const fetchDashboardData = async () => {
    try {
      const response = await api.get('/teacher/dashboard');
      setDashboardData(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center space-x-3">
              <div className="h-10 w-10 bg-white rounded-lg flex items-center justify-center p-0.5 shadow-sm border border-gray-100">
                <img src="/dashboard_logo.png" alt="Polyglots" className="h-full w-full object-contain" />
              </div>
              <div>
                <div className="text-lg font-bold text-gray-900 leading-none">Polyglots</div>
                <div className="text-[10px] text-gray-500 font-bold uppercase tracking-wider mt-0.5">Teacher Portal</div>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <select className="px-3 py-1 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                <option value="en">English</option>
                <option value="de">Deutsch</option>
              </select>
              <button
                onClick={handleLogout}
                className="flex items-center px-3 py-2 text-xs hover:bg-gray-100 rounded-md transition-colors"
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">
            Welcome Back, {user?.name || 'John Teacher'}!
          </h1>
          <p className="text-gray-600">
            Manage your batches, track student progress, and upload training content.
          </p>
        </div>

        {/* Stats Cards */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex flex-col items-center text-center">
              <BookOpen className="h-8 w-8 text-blue-600 mb-2" />
              <div className="text-2xl font-bold text-gray-900">2</div>
              <div className="text-sm text-gray-500">My Batches</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex flex-col items-center text-center">
              <Users className="h-8 w-8 text-green-600 mb-2" />
              <div className="text-2xl font-bold text-gray-900">2</div>
              <div className="text-sm text-gray-500">My Students</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex flex-col items-center text-center">
              <Award className="h-8 w-8 text-purple-600 mb-2" />
              <div className="text-2xl font-bold text-gray-900">1</div>
              <div className="text-sm text-gray-500">Content Created</div>
            </div>
          </div>

          <div className="bg-white rounded-xl shadow p-6 hover:shadow-lg transition-shadow">
            <div className="flex flex-col items-center text-center">
              <TrendingUp className="h-8 w-8 text-orange-600 mb-2" />
              <div className="text-2xl font-bold text-gray-900">87%</div>
              <div className="text-sm text-gray-500">Avg Progress</div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="mb-6">
          <div className="inline-flex h-9 items-center justify-center rounded-lg bg-gray-200 p-1">
            <button
              onClick={() => navigate('/teacher/dashboard')}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-all ${activeTab === 'dashboard'
                ? 'bg-white text-gray-900 shadow'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              Overview
            </button>
            <button
              onClick={() => navigate('/teacher/batches')}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-all ${activeTab === 'batches'
                ? 'bg-white text-gray-900 shadow'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              My Batches
            </button>
            <button
              onClick={() => navigate('/teacher/students')}
              className={`px-3 py-1 text-sm font-medium rounded-md transition-all ${activeTab === 'students'
                ? 'bg-white text-gray-900 shadow'
                : 'text-gray-600 hover:text-gray-900'
                }`}
            >
              My Students
            </button>
          </div>
        </div>

        {/* Overview Tab */}
        {activeTab === 'dashboard' && (
          <div className="grid lg:grid-cols-2 gap-6">
            {/* Active Batches */}
            <div className="bg-white rounded-xl shadow">
              <div className="p-6 border-b border-gray-200">
                <h3 className="font-semibold">My Active Batches</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {/* Batch 1 */}
                  <div className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-semibold text-gray-900">Batch A - 2025</div>
                        <div className="text-sm text-gray-500">2025-09-01 - 2025-12-31</div>
                      </div>
                      <span className="px-2.5 py-0.5 text-xs font-semibold bg-blue-100 text-blue-800 rounded">
                        15 students
                      </span>
                    </div>
                    <button className="w-full mt-2 px-3 py-2 text-xs border border-gray-300 rounded hover:bg-gray-50">
                      View Details
                    </button>
                  </div>

                  {/* Batch 2 */}
                  <div className="p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex justify-between items-start mb-2">
                      <div>
                        <div className="font-semibold text-gray-900">Batch B - 2025</div>
                        <div className="text-sm text-gray-500">2025-10-01 - 2026-01-31</div>
                      </div>
                      <span className="px-2.5 py-0.5 text-xs font-semibold bg-blue-100 text-blue-800 rounded">
                        12 students
                      </span>
                    </div>
                    <button className="w-full mt-2 px-3 py-2 text-xs border border-gray-300 rounded hover:bg-gray-50">
                      View Details
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Student Progress */}
            <div className="bg-white rounded-xl shadow">
              <div className="p-6 border-b border-gray-200">
                <h3 className="font-semibold">Student Progress</h3>
              </div>
              <div className="p-6">
                <div className="space-y-4">
                  {/* Student 1 */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-semibold text-gray-900">Jane Candidate</div>
                        <div className="text-sm text-gray-500">Batch A - 2025</div>
                      </div>
                      <span className="text-sm font-bold text-blue-600">85%</span>
                    </div>
                    <div className="w-full bg-blue-100 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full" style={{ width: '85%' }}></div>
                    </div>
                  </div>

                  {/* Student 2 */}
                  <div className="space-y-2">
                    <div className="flex justify-between items-center">
                      <div>
                        <div className="font-semibold text-gray-900">Sarah Johnson</div>
                        <div className="text-sm text-gray-500">Batch B - 2025</div>
                      </div>
                      <span className="text-sm font-bold text-blue-600">95%</span>
                    </div>
                    <div className="w-full bg-blue-100 rounded-full h-2">
                      <div className="bg-blue-600 h-2 rounded-full" style={{ width: '95%' }}></div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Batches Tab */}
        {activeTab === 'batches' && (
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="text-xl font-bold mb-4">My Batches</h3>
            <div className="text-gray-600">
              <p>Batch management functionality will be displayed here.</p>
              <p className="mt-2">Create, edit, and manage your training batches.</p>
            </div>
          </div>
        )}

        {/* Students Tab */}
        {activeTab === 'students' && (
          <div className="bg-white rounded-xl shadow p-6">
            <h3 className="text-xl font-bold mb-4">My Students</h3>
            <div className="text-gray-600">
              <p>Student management functionality will be displayed here.</p>
              <p className="mt-2">View all your students, track their progress, and manage assessments.</p>
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default TeacherDashboard;
